import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, LockKeyhole, X } from "lucide-react";
import type { MarketAnalysis } from "../domain/types";
import { validateSubject } from "../domain/validation";
import type { MarketAnalysisController } from "../sync/useMarketAnalysisSync";
import { ComparablesStep } from "./steps/ComparablesStep";
import { ResultStep } from "./steps/ResultStep";
import { StrategyStep } from "./steps/StrategyStep";
import { SubjectStep } from "./steps/SubjectStep";

const STEPS = ["subject", "comparables", "result", "strategy"] as const;
type MarketStep = (typeof STEPS)[number];
const STEP_LABELS: Record<MarketStep, string> = {
  subject: "Subject",
  comparables: "Comparables",
  result: "Result",
  strategy: "Strategy"
};

const initialStep = (analysis: MarketAnalysis): MarketStep => {
  if (analysis.status === "finalized" || analysis.resultSnapshot) return "result";
  if (analysis.comparables.length > 0 || validateSubject(analysis.subjectSnapshot).length === 0) return "comparables";
  return "subject";
};

type MarketAnalysisFlowProps = {
  analysis: MarketAnalysis;
  controller: MarketAnalysisController;
  onClose: () => void;
};

export function MarketAnalysisFlow({ analysis, controller, onClose }: MarketAnalysisFlowProps) {
  const [step, setStep] = useState<MarketStep>(() => initialStep(analysis));
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmFinalization, setConfirmFinalization] = useState(false);
  const readOnly = analysis.status === "finalized";
  const stepIndex = STEPS.indexOf(step);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const next = () => {
    setErrors([]);
    if (step === "subject") {
      const subjectErrors = validateSubject(analysis.subjectSnapshot);
      if (subjectErrors.length > 0) return setErrors(subjectErrors);
      return setStep("comparables");
    }
    if (step === "comparables") {
      if (!analysis.comparables.some((comparable) => comparable.decision !== "excluded")) return setErrors(["Použi aspoň jednu porovnateľnú nehnuteľnosť."]);
      const calculated = controller.calculateResult(analysis.id);
      if (!calculated?.resultSnapshot?.recommendedListingPrice) return setErrors(["Z dostupných cien sa nepodarilo vypočítať odporúčanú cenu."]);
      return setStep("result");
    }
    if (step === "result") return setStep("strategy");
    if (readOnly) return onClose();
    setConfirmFinalization(true);
  };

  const finalize = () => {
    const result = controller.finalizeAnalysis(analysis.id);
    if (result.errors.length > 0) {
      setErrors(result.errors);
      setConfirmFinalization(false);
      return;
    }
    setConfirmFinalization(false);
  };

  const primaryLabel = useMemo(() => {
    if (step === "subject") return "Pokračovať na porovnania";
    if (step === "comparables") return "Vypočítať výsledok";
    if (step === "result") return "Zobraziť stratégiu";
    return readOnly ? "Zavrieť analýzu" : "Dokončiť cenovú analýzu";
  }, [readOnly, step]);

  return (
    <section className="market-flow-shell" role="dialog" aria-modal="true" aria-label={`Cenová analýza v${analysis.version}`}>
      <header className="market-flow-header">
        <button type="button" onClick={onClose} aria-label="Zavrieť cenovú analýzu"><X size={20} /></button>
        <div><span>FISPRO MARKET · v{analysis.version}</span><strong>{readOnly ? "Dokončená analýza" : "Cenová analýza trhu"}</strong></div>
        <span className={`market-status ${readOnly ? "finalized" : ""}`}>{readOnly ? <LockKeyhole size={13} /> : null}{readOnly ? "FINAL" : "DRAFT"}</span>
      </header>

      <nav className="market-progress" aria-label="Kroky cenovej analýzy">
        {STEPS.map((item, index) => (
          <button
            type="button"
            key={item}
            className={item === step ? "active" : index < stepIndex ? "complete" : ""}
            onClick={() => {
              if (readOnly || index <= stepIndex || analysis.resultSnapshot) {
                setErrors([]);
                setStep(item);
              }
            }}
          >
            <span>{index < stepIndex ? <Check size={12} /> : index + 1}</span>
            {STEP_LABELS[item]}
          </button>
        ))}
      </nav>

      <main className="market-flow-content">
        {step === "subject" ? (
          <SubjectStep subject={analysis.subjectSnapshot} readOnly={readOnly} errors={errors} onChange={(subject) => controller.updateSubject(analysis.id, subject)} />
        ) : null}
        {step === "comparables" ? (
          <ComparablesStep
            subject={analysis.subjectSnapshot}
            comparables={analysis.comparables}
            marketFunnelSnapshot={analysis.marketFunnelSnapshot}
            readOnly={readOnly}
            errors={errors}
            onUpsert={(draft, id) => controller.upsertComparable(analysis.id, draft, id)}
            onRemove={(id) => controller.removeComparable(analysis.id, id)}
            onDecision={(id, decision, reason) => controller.setComparableDecision(analysis.id, id, decision, reason)}
            onAnalyze={() => controller.analyzeMarket(analysis.id)}
            onImportRealityRadar={(file) => controller.importRealityRadar(analysis.id, file)}
          />
        ) : null}
        {step === "result" ? <ResultStep subject={analysis.subjectSnapshot} comparables={analysis.comparables} marketFunnelSnapshot={analysis.marketFunnelSnapshot} result={analysis.resultSnapshot} /> : null}
        {step === "strategy" ? <StrategyStep strategies={analysis.strategySnapshot} /> : null}
      </main>

      <footer className="market-flow-actions">
        {stepIndex > 0 ? <button className="market-secondary-button" type="button" onClick={() => setStep(STEPS[stepIndex - 1])}><ArrowLeft size={17} /> Späť</button> : null}
        <button className="market-primary-button" type="button" onClick={next}>{primaryLabel}</button>
      </footer>

      {confirmFinalization ? (
        <div className="market-confirm-backdrop">
          <section className="market-confirm-panel" role="alertdialog" aria-modal="true" aria-labelledby="market-finalize-title">
            <LockKeyhole size={22} />
            <h2 id="market-finalize-title">Dokončiť analýzu v{analysis.version}?</h2>
            <p>Subject, porovnania, výsledok a stratégie sa uzamknú ako historický snapshot.</p>
            <div><button className="market-secondary-button" type="button" onClick={() => setConfirmFinalization(false)}>Ešte upraviť</button><button className="market-primary-button" type="button" onClick={finalize}>Dokončiť</button></div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
